import React, { useEffect, useState, useRef } from "react";
import { X, Menu, ArrowRight, Home, MapPin, PartyPopper, Briefcase, Heart, Pizza, Camera, Mail } from "lucide-react";

// --- KONFIGURATION & DATEN ---
const LOGO_URL = "https://sure-fly.10web.cloud/wp-content/uploads/2026/01/Namo_Logo_Transparent-1.png";
const HEADER_OFFSET = 100;
const MENU_ITEMS = [{
  id: "1",
  label: "Startseite",
  href: "/",
  color: "bg-[#FFF8E1]"
}, {
  id: "2",
  label: "Startpunkt",
  href: "https://namo-catering.de/startpunkt/",
  color: "bg-[#F5FBEF]"
}, {
  id: "3",
  label: "Private Events",
  href: "https://namo-catering.de/privateevents/",
  color: "bg-[#FFF1D6]"
}, {
  id: "4",
  label: "Berufliche Events",
  href: "https://namo-catering.de/berufliche-events/",
  color: "bg-[#EEF6E5]"
}, {
  id: "5",
  label: "Hochzeiten",
  href: "https://namo-catering.de/hochzeit/",
  color: "bg-[#FFF8E1]"
}, {
  id: "6",
  label: "Pizza Workshop",
  href: "https://namo-catering.de/neapolitanische-pizza-workshop/",
  color: "bg-[#F5FBEF]"
}, {
  id: "7",
  label: "Eindrücke",
  href: "https://namo-catering.de/pizza-event-experiences-2/",
  color: "bg-[#FFF1D6]"
}, {
  id: "8",
  label: "Kontakt",
  href: "https://namo-catering.de/kontaktaufnahme/",
  color: "bg-[#EEF6E5]"
}];

// --- ICONS PRO KARTE (dezente Background-Symbole) ---
const ITEM_ICONS = [Home,
// Startseite
MapPin,
// Startpunkt
PartyPopper,
// Private Events
Briefcase,
// Berufliche Events
Heart,
// Hochzeiten
Pizza,
// Pizza Workshop
Camera,
// Eindrücke
Mail // Kontakt
];

// --- HILFSKOMPONENTEN ---
/**
 * NavButton ist jetzt ein reiner Button-Typ.
 * Er wickelt die Navigation (Anker oder Link) intern über JavaScript ab.
 */
const NavButton = ({
  to,
  children,
  className,
  onClick
}) => {
  const handleClick = e => {
    if (to.startsWith("#")) {
      e.preventDefault();
      const id = to.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        window.scrollTo({
          top: el.offsetTop - HEADER_OFFSET,
          behavior: "smooth"
        });
      }
    } else {
      // Manuelle Navigation für den Button
      window.location.href = to;
    }
    // Führe das übergebene onClick (z.B. Menü schließen) aus
    if (onClick) onClick(e);
  };
  return <button type="button" onClick={handleClick} className={className}>
      {children}
    </button>;
};

// --- HAUPTKOMPONENTE ---
export default function App() {
  const [navOpen, setNavOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Header Hide/Show Logik beim Scrollen
  useEffect(() => {
    const handleScroll = () => {
      if (navOpen) return;
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [navOpen]);

  // Scroll-Lock bei offenem Menü
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.style.overflow = navOpen ? "hidden" : "unset";
    }
  }, [navOpen]);
  return <div className="w-full font-sans overflow-x-hidden bg-[#436C1A]">
      {/* HEADER */}
      <header style={{
      backgroundColor: "rgba(67, 108, 26, 0.98)"
    }} // Namo Green
    className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 border-b border-white/10 ${isHeaderVisible ? "translate-y-0 py-4 md:py-12" : "-translate-y-full py-4 md:py-12"}`}>
        <div className="max-w-[1600px] mx-auto px-6 flex items-center justify-between relative">
          {/* Brand */}
          <div className="flex-1 text-left">
            <a href="/" className="group inline-block">
              <h1 className="text-sm md:text-4xl lg:text-5xl font-black uppercase tracking-tighter text-white group-hover:text-[#FDC12F] transition-colors leading-none">
                Namo Catering
              </h1>
              <p className="text-[8px] md:text-2xl font-bold italic text-[#FDC12F] tracking-wide mt-1">
                ... der mit dem Leopard!
              </p>
            </a>
          </div>

          {/* Logo Central */}
          <div className="flex-shrink-0 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <img src={LOGO_URL} alt="Namo Logo" className="h-16 md:h-28 lg:h-36 w-auto object-contain drop-shadow-xl" />
          </div>

          {/* Navigation Toggle */}
          <div className="flex-1 flex justify-end">
            <button onClick={() => setNavOpen(true)} className="group flex items-center gap-3 bg-white/10 hover:bg-[#FDC12F] text-white hover:text-black px-5 md:px-8 py-2 md:py-4 rounded-full border border-white/20 transition-all duration-300 shadow-lg">
              <span className="hidden md:inline font-black uppercase text-sm tracking-[0.2em]">Navigation</span>
              <Menu size={24} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>
      </header>

      {/* Spacer in Grün gegen weiße Zeilen */}
      <div className="h-24 md:h-48 bg-[#436C1A]" />

      {/* FULLSCREEN NAVIGATION WINDOW */}
      <div className={`fixed inset-0 z-[100] bg-[#436C1A] flex flex-col transition-all duration-700 ease-[cubic-bezier(0.85,0,0.15,1)] ${navOpen ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"}`}>
        {/* Modal Header */}
        <div className="w-full max-w-[1700px] mx-auto p-4 md:p-6 flex justify-end items-center text-white">
          <button onClick={() => setNavOpen(false)} className="flex items-center gap-3 bg-white/10 hover:bg-white text-white hover:text-[#436C1A] px-4 py-2 rounded-full border border-white/20 transition-all group active:scale-90 shadow-2xl">
            <span className="font-black uppercase text-xs tracking-widest text-white group-hover:text-[#436C1A]">Schließen</span>
            <X size={20} className="group-hover:rotate-90 transition-transform duration-500" />
          </button>
        </div>

        {/* Navigation Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Wichtig: auf Mobile oben starten statt hart zu zentrieren */}
          <div className="min-h-full w-full max-w-6xl mx-auto flex flex-col items-center px-2 md:px-4 pb-6">
            {/* 1. Welcome Header (auf Mobile IMMER sichtbar) */}
            <div className="w-full flex flex-col items-center mt-2 md:mt-0 mb-4 md:mb-8 animate-in fade-in slide-in-from-top-4 duration-1000">
              <div className="flex items-center gap-4 md:gap-8 mb-3">
                <img src={LOGO_URL} alt="Namo Logo" className="hidden md:block h-16 md:h-28 lg:h-36 xl:h-44 w-auto object-contain drop-shadow-2xl" />
                <p className="text-xl md:text-3xl lg:text-4xl xl:text-5xl italic font-light text-white/95 drop-shadow-lg tracking-wide leading-tight">
                  Herzlich willkommen!
                </p>
              </div>
              <div className="h-2 w-24 md:w-40 bg-[#FDC12F] rounded-full shadow-[0_0_15px_rgba(253,193,47,0.5)]" />
            </div>

            {/* 2. Grid Container */}
            <div className="w-full max-w-4xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                {MENU_ITEMS.map((item, index) => {
                const BgIcon = ITEM_ICONS[index] || ArrowRight;
                return <NavButton key={item.id} to={item.href} onClick={() => setNavOpen(false)} className={`
                        ${item.color} group relative flex flex-col justify-between 
                        aspect-[1.1/1] p-4 md:p-5 rounded-[1.25rem] md:rounded-[1.75rem] 
                        shadow-[0_10px_25px_rgba(0,0,0,0.15)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)]
                        border border-black/5 hover:border-[#FDC12F]
                        transition-all duration-300 hover:-translate-y-1 text-left
                        overflow-hidden

                        /* Touch/Press Effekt (statt Hover) */
                        active:scale-[0.98] active:shadow-[0_12px_28px_rgba(0,0,0,0.22)]
                        focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FDC12F]/40
                      `}>
                      {/* "Shine" Effekt beim Tippen (sichtbar auf Mobile über active) */}
                      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity duration-300">
                        <div className="absolute -top-12 -left-12 w-52 h-52 rotate-12 bg-white/30 blur-2xl" />
                      </div>

                      {/* Dezentes Background-Icon: auf Touch etwas präsenter */}
                      <div className="pointer-events-none absolute -bottom-6 -right-6 opacity-[0.16] md:opacity-10 group-hover:opacity-[0.18] md:group-hover:opacity-15 active:opacity-[0.20] transition-opacity duration-300">
                        <BgIcon className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 text-[#C48E00]" />
                      </div>

                      {/* Nummer */}
                      <span className="relative z-[1] text-xl sm:text-2xl md:text-xl lg:text-2xl font-black text-[#C48E00]/30 group-hover:text-[#C48E00] transition-colors">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      {/* Label */}
                      <div className="relative z-[1] flex flex-col gap-1">
                        <h3 className="text-base sm:text-lg md:text-lg lg:text-xl font-black uppercase leading-tight text-slate-800 tracking-tighter">
                          {item.label}
                        </h3>

                        {/* Bar */}
                        <div className="h-[5px] sm:h-1 w-10 sm:w-8 bg-[#FDC12F] rounded-full group-hover:w-full active:w-full transition-all duration-500 ease-out" />
                      </div>

                      {/* Arrow: auf Mobile immer sichtbar, auf Desktop bleibt Hover-Feeling */}
                      <div className="absolute top-4 right-4 opacity-80 group-hover:opacity-100 transition-all">
                        <ArrowRight className="text-[#C48E00] size-5 md:size-5" />
                      </div>
                    </NavButton>;
              })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}